import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { StorageKey } from '../auth/DataService';

/**
 * Centralized user preferences context.
 *
 * Loads the full preferences object from the backend once on mount and
 * provides helpers to read & update individual keys.  Every update is
 * optimistically applied to local state and then persisted to the
 * backend via the merge‑capable PUT /lp/users/{emea}/preferences
 * endpoint (only the changed key is sent; existing keys are preserved
 * server‑side).
 *
 * Currently supported preference keys (extend as needed):
 *   - theme:                         'dark' | 'light'
 *   - hiddenVesselReportingColumns:  string[]
 *   - dateFormat:                    string (e.g. 'DD.MM.YYYY')
 *   - vesselReportingColumnOrder:    string[] (field names in display order; empty = default)
 *   - vesselReportingColumnWidths:    { [field]: pixelWidth } (empty = use colDef widths)
 *   - vesselReportingPinnedColumns:   { left: string[], right: string[] }
 */

const DEFAULT_PREFERENCES = {
  theme: 'dark',
  hiddenVesselReportingColumns: ['id'],
  dateFormat: 'DD.MM.YYYY',
  vesselReportingColumnOrder: [],
  vesselReportingColumnWidths: {},
  vesselReportingPinnedColumns: { left: ['actualEtd'], right: ['action'] },
  hiddenStockTableColumns: [],
  stockTableColumnOrder: [],
  stockTableColumnWidths: {},
  stockTablePinnedColumns: { left: ['date'] },
};

const UserPreferencesContext = createContext(null);

const UserPreferencesProvider = ({ children, fetchUser, savePreferences }) => {
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [loading, setLoading] = useState(true);

  const emea = localStorage.getItem(StorageKey.userInfo_sub);

  // ── Load preferences from backend on mount ──────────────────────
  useEffect(() => {
    if (!emea) {
      setLoading(false);
      return;
    }

    let mounted = true;

    (async () => {
      try {
        const user = await fetchUser(emea);
        if (!mounted) return;
        const saved = JSON.parse(user.preferences || '{}');
        setPreferences((prev) => ({ ...prev, ...saved }));
      } catch (err) {
        console.error(
          'UserPreferencesContext – failed to load preferences:',
          err
        );
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [emea, fetchUser]);

  // ── Persist a single preference key (shallow merge on backend) ──
  const updatePreference = useCallback(
    async (key, value) => {
      // Optimistic local update
      setPreferences((prev) => ({ ...prev, [key]: value }));

      if (!emea) {
        console.warn('UserPreferencesContext – no EMEA; change is local‑only.');
        return;
      }

      try {
        await savePreferences(emea, { [key]: value });
      } catch (err) {
        console.error(`UserPreferencesContext – failed to save "${key}":`, err);
        // Note: we do NOT revert the local state here to avoid flicker,
        // but you could add rollback logic if desired.
      }
    },
    [emea, savePreferences]
  );

  // ── Persist multiple preference keys at once ────────────────────
  const updatePreferences = useCallback(
    async (partial) => {
      setPreferences((prev) => ({ ...prev, ...partial }));

      if (!emea) {
        console.warn('UserPreferencesContext – no EMEA; change is local‑only.');
        return;
      }

      try {
        await savePreferences(emea, partial);
      } catch (err) {
        console.error(
          'UserPreferencesContext – failed to save preferences:',
          err
        );
      }
    },
    [emea, savePreferences]
  );

  return (
    <UserPreferencesContext.Provider
      value={{ preferences, updatePreference, updatePreferences, loading }}
    >
      {children}
    </UserPreferencesContext.Provider>
  );
};

/**
 * Hook to consume user preferences from any component.
 *
 * Returns:
 *   preferences        – the full preferences object
 *   updatePreference    – (key, value) => Promise<void>
 *   updatePreferences   – (partial)    => Promise<void>
 *   loading             – true while the initial fetch is in progress
 */
const useUserPreferences = () => {
  const ctx = useContext(UserPreferencesContext);
  if (ctx === null) {
    throw new Error(
      'useUserPreferences must be used within a <UserPreferencesProvider>'
    );
  }
  return ctx;
};

export { UserPreferencesProvider, useUserPreferences, DEFAULT_PREFERENCES };
